import React from "react";
import { env, STRIPE_CUSTOMER_PORTAL, useGlobalState } from "../helpers/GlobalStateProvider";
import { openPortalOnClick } from "../helpers/stripePortal";
import axios from "axios";
import { getApiUrl } from "../config";
import { useModalState } from "../helpers/ModalProvider";
import { Box, Card, Flex, IconButton, Link, Table, Text as ChakraText, VStack } from "@chakra-ui/react";
import { TIER_CREDITS } from "../helpers/tierConstants";
import { LuArrowRight, LuCopy, LuPencil, LuPlus, LuTrash } from "react-icons/lu";
import { Button } from "../components/ui/button";
import { getToken } from "../helpers/tokenManager";
import { toaster } from "../components/ui/toaster";

const APIKeysContainer: React.FC = () => {
  const { info, refreshAccountInfo, isSignedIn } = useGlobalState();
  const { showDeleteModal, showEditModal, showErrorMessage } = useModalState();

  const handleCreateApiKey = async () => {
    try {
      const bearerToken = getToken();
      await axios.post(
        `${getApiUrl(env)}/v3/apiKey/registerApiKey`,
        {},
        { headers: { Authorization: `Bearer ${bearerToken}` } },
      );
      await refreshAccountInfo();
    } catch (error) {
      showErrorMessage("Error creating API key");
    }
  };

  const formatLastUsed = (date: string) => {
    const now = new Date();
    const lastUsed = new Date(date);
    const diffInSeconds = Math.floor(
      (now.getTime() - lastUsed.getTime()) / 1000,
    );

    if (diffInSeconds < 60) {
      return `${diffInSeconds} seconds ago`;
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minutes ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hours ago`;
    }

    return lastUsed.toLocaleDateString("en-CA");
  };

  const handleCopyClick = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      toaster.create({
        type: "success",
        title: "API key copied to clipboard",
        duration: 3000,
      });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error("Failed to copy:", err);
    }
  };

  // Filter and sort API keys
  const apiKeys = info?.apiKeys
    ?.filter((k: any) => k.enabled)
    ?.sort(
      (a: any, b: any) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

  return (
    <Card.Root>
      <Card.Header flexDir={{ base: 'column', md: 'row' }} gap={4} justifyContent='space-between' alignItems='flex-start'>
        <VStack alignItems='flex-start' gap="12px">
          <Card.Title fontSize="30px" fontWeight="bold" letterSpacing="-0.4px" lineHeight="32px">My API keys</Card.Title>
          <Card.Description fontSize="md" color="#52525b" lineHeight="24px">View and manage your API keys:</Card.Description>
        </VStack>
        <Button variant='solid' colorPalette='gray' disabled={!isSignedIn()} onClick={isSignedIn() ? handleCreateApiKey : undefined}>
          <LuPlus />
          Create API Key
          </Button>
      </Card.Header>

      <Card.Body>
        {/* Show table only if there are API keys */}
      {apiKeys && apiKeys.length > 0 && (
        <>
          {/* Table Container (Header + Rows). ScrollArea lets the wide table
              scroll horizontally within the card on mobile instead of pushing
              the page past the viewport. */}
          <Table.ScrollArea borderWidth="0" w="100%">
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader fontSize="14px" fontWeight={500} color="#a1a1aa">Label</Table.ColumnHeader>
                <Table.ColumnHeader fontSize="14px" fontWeight={500} color="#a1a1aa">API Key</Table.ColumnHeader>
                <Table.ColumnHeader fontSize="14px" fontWeight={500} color="#a1a1aa">Credits Used</Table.ColumnHeader>
                <Table.ColumnHeader fontSize="14px" fontWeight={500} color="#a1a1aa">Last Used</Table.ColumnHeader>
                <Table.ColumnHeader fontSize="14px" fontWeight={500} color="#a1a1aa">Created On</Table.ColumnHeader>
                <Table.ColumnHeader fontSize="14px" fontWeight={500} color="#a1a1aa">Actions</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
            {apiKeys.map((k: any) => (
              <Table.Row key={k.key}>
                {/* Label */}
                <Table.Cell>
                  <span
                    style={{ cursor: "pointer" }}
                  onClick={() => showEditModal(k.key, k.label)}
                >
                  {k.label}
                  </span>
                </Table.Cell>

                {/* API Key + copy icon */}
                <Table.Cell>
                <span style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ marginRight: 8 }}>{k.key.slice(0, 24)}...</div>
                  <IconButton variant='ghost' size='sm' aria-label="Copy API key" onClick={() => handleCopyClick(k.key)}>
                    <LuCopy />
                    </IconButton>
                  </span>
                </Table.Cell>

                {/* Credits Used */}
                <Table.Cell>
                  <span>{k.monthly_usage}</span>
                </Table.Cell>

                {/* Last Used */}
                <Table.Cell>
                  <span>{formatLastUsed(k.updatedAt)}</span>
                </Table.Cell>

                {/* Created On */}
                <Table.Cell>
                  <span>{new Date(k.createdAt).toLocaleDateString("en-CA")}</span>
                </Table.Cell>

                {/* Actions: side-by-side */}
                <Table.Cell>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      justifyContent: "flex-end",
                    }}
                >
                  <IconButton size='sm' variant='ghost' aria-label="Edit API key" onClick={() => showEditModal(k.key, k.label)}>
                    <LuPencil />
                  </IconButton>
                  <IconButton size='sm' variant='ghost' aria-label="Delete API key" onClick={() => showDeleteModal(k.key, k.label)}>
                    <LuTrash />
                  </IconButton> 
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
            </Table.Body>
          </Table.Root>
          </Table.ScrollArea>
        </>
      )}

      </Card.Body>
      {/* Empty state when no API keys */}
      {(!apiKeys || apiKeys.length === 0) && (
        <Card.Body>
          <Flex direction="column" alignItems="center" justifyContent="center" py={8} gap={4}>
            <ChakraText fontSize="lg" fontWeight="semibold" color="#18181b">No API keys yet</ChakraText>
            <ChakraText fontSize="sm" color="#71717a" textAlign="center">
              Create your first API key to start making requests to the CoinCap API.
            </ChakraText>
            <Button
              variant='solid'
              colorPalette='gray'
              disabled={!isSignedIn()}
              onClick={isSignedIn() ? handleCreateApiKey : undefined}
            >
              <LuPlus />
              Create API Key
            </Button>
          </Flex>
        </Card.Body>
      )}

      {/* Credits usage footer */}
      {(() => {
        const tier = info?.tier ?? 0;
        const stripeTier = info?.stripeTier;
        const effectiveTier = stripeTier ?? tier;
        const apiKeyUsage =
          info?.apiKeys?.reduce(
            (sum: number, key: any) => sum + (key?.monthly_usage ?? 0),
            0,
          ) ?? 0;
        const usedCredits = info?.creditsUsed ?? apiKeyUsage;
        const totalCredits = info?.credits ?? TIER_CREDITS[effectiveTier] ?? 500;
        const pct = totalCredits > 0 ? Math.min((usedCredits / totalCredits) * 100, 100) : 0;
        const barColor = pct >= 90 ? "#ef4444" : pct >= 75 ? "#f59e0b" : "#22c55e";

        return (
          <Card.Footer p={{ base: 4, md: 6 }} borderTop="1px solid" borderColor="#e4e4e7">
            <Flex direction={{ base: "column", md: "row" }} gap={4} alignItems={{ base: "stretch", md: "center" }} w="100%">
              <Flex flex={1} direction="column" gap={1}>
                <Flex justifyContent="space-between" alignItems="baseline">
                  <ChakraText fontSize="sm" fontWeight="semibold" color="#18181b">
                    Credits used this month
                  </ChakraText>
                  <ChakraText fontSize="sm" fontWeight="semibold" color="#18181b">
                    {usedCredits.toLocaleString()} / {totalCredits.toLocaleString()}
                  </ChakraText>
                </Flex>
                <Box w="100%" bg="#f4f4f5" borderRadius="full" h="8px" overflow="hidden">
                  <Box
                    bg={barColor}
                    h="100%"
                    w={`${pct}%`}
                    borderRadius="full"
                    transition="width 0.3s ease"
                  />
                </Box>
                {usedCredits >= totalCredits ? (
                  <Flex bg="#fef2f2" border="1px solid #fecaca" borderRadius="6px" px={3} py={2} mt={1} alignItems="center" gap={2}>
                    <ChakraText fontSize="xs" fontWeight="semibold" color="#dc2626">
                      Monthly credit limit reached.{" "}
                      {effectiveTier !== 8 && effectiveTier !== 12 && (
                        <Link href={STRIPE_CUSTOMER_PORTAL} target="_blank" rel="noopener noreferrer" onClick={openPortalOnClick} color="#dc2626" textDecoration="underline">
                          Upgrade your plan to continue.
                        </Link>
                      )}
                    </ChakraText>
                  </Flex>
                ) : (
                  <ChakraText fontSize="xs" color="#a1a1aa" mt={1}>
                    {pct < 75
                      ? "Usage is healthy."
                      : pct < 90
                      ? "Approaching credit limit."
                      : "Nearing credit limit. Consider upgrading."}
                  </ChakraText>
                )}
              </Flex>
              {effectiveTier !== 8 && effectiveTier !== 12 && (
                isSignedIn() ? (
                  <Button size="sm" variant="outline" colorPalette="purple" asChild>
                    <Link href={STRIPE_CUSTOMER_PORTAL} target="_blank" rel="noopener noreferrer" onClick={openPortalOnClick}>
                      Upgrade Plan <LuArrowRight />
                    </Link>
                  </Button>
                ) : (
                  // Signed out: render disabled, matching how the tier signup
                  // buttons on the dashboard are also disabled until sign-in.
                  // Stripe's portal login page silently fails for unknown
                  // emails, so we don't want to send anonymous users there.
                  <Button size="sm" variant="outline" colorPalette="purple" disabled>
                    Upgrade Plan <LuArrowRight />
                  </Button>
                )
              )}
            </Flex>
          </Card.Footer>
        );
      })()}
    </Card.Root>
  );
};

export default APIKeysContainer;
